import { Injectable } from '@angular/core';
import { Set } from 'immutable';

import { TreeNode } from '../../core/interface/core.interface';
import { CoreService } from '../../core/service/core.service';
import { CoreUtilities } from '../../core/utilities/core.utilities';
import { NODES_TYPE_DATATARGET, NODES_TYPE_NODETYPEGROUP, NODES_TYPE_TRAVERSER } from '../../shared/api/nodes/nodes.models';
import { AppGlobal } from '../../app.global';
import { Traverser } from '../traverser/traverser';
import { RequestDiffRecord } from '../../shared';
import { UUID } from 'angular2-uuid';

@Injectable({
  providedIn: 'root'
})
export class AI {

  public coreService: CoreService;

  protected findPreRunNodesDebounce: number;
  protected findPreRunNodesCallbackMap = {};
  protected findPreRunNodesRequest = [];

  public constructor(protected coreUtilities: CoreUtilities) {}

  public getKey(configurationNode: TreeNode, treeNode: TreeNode) {
    /* Set the prompt */
    let prompt = this.coreUtilities.unescapeHtml(configurationNode.description);
    if (treeNode !== undefined && prompt.includes('{{SEARCH_VALUE}}')) {
      prompt = prompt.replace('{{SEARCH_VALUE}}', treeNode.name);
    }

    /* Generate system prompt */
    const systemPrompt = this.coreUtilities.unescapeHtml(configurationNode.description1);

    /* Get nodes */
    const nodes = this.getNodes(configurationNode, treeNode);

    return prompt.replace(/\s+|\n/gi, '') + systemPrompt + nodes.map(node => '#node:' + node.id + '#').sort((a, b) => a.localeCompare(b)).join('');
  }

  public findPreRunNodes(requestId: string, raw: boolean, callback: Function) {
    if (this.findPreRunNodesDebounce !== undefined) {
      window.clearTimeout(this.findPreRunNodesDebounce);
    }
    this.findPreRunNodesCallbackMap[requestId] = callback;
    this.findPreRunNodesRequest.push(requestId);
    this.findPreRunNodesDebounce = window.setTimeout(() => this.runFindPreRunNodes(raw), 50);
  }

  private runFindPreRunNodes(raw: boolean) {
    /* Ids */
    const ids = this.findPreRunNodesRequest;
    /* Clear */
    this.findPreRunNodesRequest = [];
    this.findPreRunNodesDebounce = undefined;
    /* Set request id */
    const id = UUID.UUID();
    /* Run request */
    this.coreService.doRequest({ id, payloads: [{ id, method: 'getLLMResult', action: 'ai', payload: { data: [{ ids, raw }] }}] }, (diff: RequestDiffRecord) => {
      const response = diff.response[id].data[0];
      const count = ids.length;
      for (let i = 0; i < count; i++) {
        const requestId = ids[i];
        /* Get the callback */
        const callback = this.findPreRunNodesCallbackMap[requestId];
        if (callback !== undefined) {
          /* Remove */
          delete this.findPreRunNodesCallbackMap[requestId];
          /* Get the response */
          let value = response[requestId];
          if (value === undefined) {
            value = [];
          }
          /* Run callback with value */
          callback(requestId, raw ? value : value.map(val => AppGlobal.treeNodes.get(val)).filter(val => !!val));
        }
      }
    });
  }

  protected getNodes(configurationNode: TreeNode, treeNode: TreeNode) {
    /* Nodes */
    let nodes: TreeNode[] = [];

    /* Node types */
    const nodeTypeNode = configurationNode.children.filter(child => child.nodeType === NODES_TYPE_NODETYPEGROUP)[0];
    if (nodeTypeNode !== undefined) {
      const nodeTypes = Set<number>(nodeTypeNode.children.map(child => child.nodeType));
      nodes = nodes.concat(AppGlobal.treeNodes.filter(node => nodeTypes.has(node.nodeType) && node.modelId === (treeNode as TreeNode).modelId).toArray());
    }

    /* Data target */
    const dataTargetNode = configurationNode.children.filter(child => child.nodeType === NODES_TYPE_DATATARGET)[0];
    if (dataTargetNode !== undefined) {
      const traverserNode = dataTargetNode.children.filter(child => child.nodeType === NODES_TYPE_TRAVERSER)[0];
      if (traverserNode !== undefined) {
        nodes = nodes.concat(new Traverser().addToLanes(traverserNode.children).setRelationshipWeights(this.coreService.getRelationshipWeights()).run([treeNode]));
      }
    }

    return nodes;
  }
}
